import React, { useState, useContext, useEffect, useRef } from 'react';
import ModuleHeaderLayout from 'components/Common/Layouts/ModuleHeaderLayout';
import { Grid, Typography } from '@mui/material';
import StepperDisplay from 'components/BiteBulk/StepperDisplay';
import ScoreIndicator from 'components/BiteBulk/ScoreIndicator';
import Table from 'components/BiteBulk/Table';
import { GlobalContext } from 'providers/GlobalProvider';
import SubscribeModal from '../../components/SubscribeModal';
import { Redirect } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { bulkReport } from 'components/Common/PDFManage';
import {
  NextButton,
  TicketButton,
  StyledInput,
} from 'components/Common/MUIStyledComponents';
import Modal from 'react-modal';

const BiteBulk: React.FC = () => {
  // PAYMENTS STATE AND FUNCTIONS
  const { cur_user } = useContext(GlobalContext);
  const [ userHasBiteBulk, setUserHasBiteBulk ] = useState(false);
  const [ activeSub, setActiveSub ] = useState(false);
  const [ matchedTeamWithStripe, setMatchedTeamWithStripe ] =
    useState<string>('');
  const { enqueueSnackbar } = useSnackbar();

  if (!cur_user.selected_team) {
    return <Redirect to='/' />;
  }

  const checkForBiteBulkAndTrial = () => {
    if (
      cur_user.selected_team?.attributes?.tier?.includes('enterprise') ||
      cur_user.selected_team?.attributes?.tier?.includes('pro')
    ) {
      setUserHasBiteBulk(true);
    }
    if (cur_user.stripe_cus_info) {
      cur_user.stripe_cus_info.map((cus: any) => {
        if (cus.name === cur_user.selected_team.name) {
          setMatchedTeamWithStripe(cus.id);
          cus.subscriptions.data.map((sub: any) => {
            if (
              sub.metadata.moduleName === 'BITE Pro' ||
              sub.metadata.moduleName === 'BITE Enterprise'
            ) {
              setActiveSub(true);
            }
          });
        }
      });
    }
  };

  useEffect(() => {
    checkForBiteBulkAndTrial();
  }, [ activeSub ]);

  // PAGE STATE AND FUNCTIONS
  const [ activeStep, setActiveStep ] = useState(0);
  const [ score, setScore ] = useState(0);
  const [ hits, setHits ] = useState({});
  const [ loading, setLoading ] = useState(false);
  const [ fileName, setFileName ] = useState('');
  const [ fileNameModalOpen, setFileNameModalOpen ] = useState(false);
  const [ resLoading, setResLoading ] = useState(false);

  const refForPdf = useRef<any>(null);

  const steps = [
    'Which template are you using?',
    'Files',
    'Match Properties',
    'Preferences',
  ];

  // PDF DOWNLOADING
  const headers = [ 'Field', 'Column Name', 'Dataset', 'Row #', 'Hits' ];
  const handleDownloadPDF = () => {
    if (Object.keys(hits).length >= 1) {
      bulkReport(fileName, refForPdf, headers, hits, true);
    } else {
      enqueueSnackbar('No Search has Run to Download Results', {
        variant: 'error',
      });
    }
  };

  const handleFileNameChange = (e: any) => {
    setFileName(e.target.value);
  };
  const handleFileNameClose = () => {
    setFileNameModalOpen(false);
    setFileName('');
  };

  const helpContent = (
    <ol className='module-header-tooltip-text'>
      <li>
        The landing page of the BITE Bulk module displays all of your uploads.
      </li>
      <br></br>
      <li>
        The upload new data, click on the “+ Upload” button in the top right
        corner. This will prompt you to upload your data file. Please note that
        only .csv files are accepted at this time.
      </li>
      <br></br>
      <li>Once the file is uploaded, click the “Next” button.</li>
      <br></br>
      <li>
        You will then need to ensure that all of your entries are matched up
        properly. For example, match up the data entry “John” with the property
        “Name”.
      </li>
      <br></br>
      <li>
        After your data is properly matched, click “Submit”. This will run your
        data through our database. Please note this may take a few moments.
      </li>
      <br></br>
      <li>
        The results shown display not just the hits, but the entirety of your
        data. To save the hits as a recurring ticket, please click the checkbox
        that says “Ticket Created” and “Daily Checks.
      </li>
      <br></br>
      <li>
        Saving as a ticket means the hit results will show up in your analytics
        vault. Enabling daily checks means the data will be run against our
        updated system every day.
      </li>
    </ol>
  );

  return (
    <ModuleHeaderLayout title='BITE Bulk' helpContent={helpContent}>
      <div>
        {!userHasBiteBulk ? (
          <SubscribeModal
            name='BITE Bulk'
            description='Get your information scored in bulk'
            price={599}
            curUser={cur_user}
            stripeTeam={matchedTeamWithStripe}
            activeSub={activeSub}
          />
        ) : (
          <></>
        )}
      </div>
      <div className={!userHasBiteBulk ? 'blur-div' : ''}>
        <Grid container spacing={2} style={{ marginTop: '10px' }}>
          <Grid item xs={12}>
            {/* <TopStepper activeStep={activeStep} steps={steps}/> */}
          </Grid>
          <Grid item xs={12} md={8}>
            <StepperDisplay
              refForPdf={refForPdf}
              loading={loading}
              setLoading={setLoading}
              activeStep={activeStep}
              setActiveStep={setActiveStep}
              steps={steps}
              setScore={setScore}
              setHits={setHits}
              setResLoading={setResLoading}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <div ref={refForPdf}>
              <ScoreIndicator score={score} />
            </div>

            <div
              className='bulk-pdf-download'
              onClick={() => {
                if (Object.keys(hits).length) {
                  setFileNameModalOpen(true);
                }
              }}
            >
              <p>Download as PDF</p>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='18'
                height='18'
                viewBox='0 0 18 18'
                fill='none'
              >
                <path
                  d='M15 6.705C14.9922 6.6361 14.9771 6.56822 14.955 6.5025V6.435C14.9189 6.35788 14.8708 6.287 14.8125 6.225L10.3125 1.725C10.2505 1.66666 10.1796 1.61856 10.1025 1.5825H10.0275C9.95457 1.5435 9.8762 1.51569 9.795 1.5H5.25C4.65326 1.5 4.08097 1.73705 3.65901 2.15901C3.23705 2.58097 3 3.15326 3 3.75V14.25C3 14.8467 3.23705 15.419 3.65901 15.841C4.08097 16.2629 4.65326 16.5 5.25 16.5H12.75C13.3467 16.5 13.919 16.2629 14.341 15.841C14.7629 15.419 15 14.8467 15 14.25V6.75C15 6.75 15 6.75 15 6.705ZM10.5 4.0575L12.4425 6H11.25C11.0511 6 10.8603 5.92098 10.7197 5.78033C10.579 5.63968 10.5 5.44891 10.5 5.25V4.0575ZM13.5 14.25C13.5 14.4489 13.421 14.6397 13.2803 14.7803C13.1397 14.921 12.9489 15 12.75 15H5.25C5.05109 15 4.86032 14.921 4.71967 14.7803C4.57902 14.6397 4.5 14.4489 4.5 14.25V3.75C4.5 3.55109 4.57902 3.36032 4.71967 3.21967C4.86032 3.07902 5.05109 3 5.25 3H9V5.25C9 5.84674 9.23705 6.41903 9.65901 6.84099C10.081 7.26295 10.6533 7.5 11.25 7.5H13.5V14.25ZM9.9675 10.7175L9.75 10.9425V9C9.75 8.80109 9.67098 8.61032 9.53033 8.46967C9.38968 8.32902 9.19891 8.25 9 8.25C8.80109 8.25 8.61032 8.32902 8.46967 8.46967C8.32902 8.61032 8.25 8.80109 8.25 9V10.9425L8.0325 10.7175C7.89127 10.5763 7.69973 10.4969 7.5 10.4969C7.30027 10.4969 7.10873 10.5763 6.9675 10.7175C6.82627 10.8587 6.74693 11.0503 6.74693 11.25C6.74693 11.4497 6.82627 11.6413 6.9675 11.7825L8.4675 13.2825C8.53883 13.3508 8.62294 13.4043 8.715 13.44C8.80478 13.4797 8.90185 13.5002 9 13.5002C9.09815 13.5002 9.19522 13.4797 9.285 13.44C9.37706 13.4043 9.46117 13.3508 9.5325 13.2825L11.0325 11.7825C11.1737 11.6413 11.2531 11.4497 11.2531 11.25C11.2531 11.0503 11.1737 10.8587 11.0325 10.7175C10.8913 10.5763 10.6997 10.4969 10.5 10.4969C10.3003 10.4969 10.1087 10.5763 9.9675 10.7175Z'
                  fill='#F5FAFF'
                />
              </svg>
            </div>
          </Grid>
          <Grid item xs={12} style={{ marginBottom: '50px' }}>
            <Table hits={hits} loading={loading} resLoading={resLoading} />
          </Grid>
        </Grid>
      </div>
      <Modal
        isOpen={fileNameModalOpen}
        onRequestClose={handleFileNameClose}
        ariaHideApp={false}
      >
        <div className='bite-score-file-modal'>
          <div className='display-flex justify-between width-100'>
            <Typography variant='h5'>File Name:</Typography>
            <TicketButton
              onClick={handleFileNameClose}
              className='cursor-pointer'
            />
          </div>
          <div className='display-flex justify-end width-100'>
            <StyledInput
              value={fileName}
              onChange={handleFileNameChange}
              variant='outlined'
              className='margin-right-20px'
            />
            <NextButton
              variant='contained'
              color='primary'
              onClick={() => {
                handleDownloadPDF();
                handleFileNameClose();
              }}
            >
              Download
            </NextButton>
          </div>
        </div>
      </Modal>
    </ModuleHeaderLayout>
  );
};

export default BiteBulk;
